import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

// Job role structure with two levels
const JOB_ROLES = {
  'Pharmacist': ['Supervising Pharmacist', 'Pharmacist', 'Per diem Pharmacist'],
  'Technician': ['Senior', 'Junior', 'Training'],
  'Pharmacy Cashier': ['Senior', 'Junior', 'Training'],
  'Pharmacy Assistant': ['Senior', 'Junior', 'Training'],
  'OTC Cashier': ['Senior', 'Junior', 'Training'],
  'DME Tech': ['Senior', 'Junior', 'Training'],
  'Office Admin': ['Senior', 'Junior', 'Training'],
};

const EmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('employees')
        .select('*')
        .order('payroll_name', { ascending: true, nullsLast: true });

      if (fetchError) {
        throw fetchError;
      }

      setEmployees(data || []);
      setError('');
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError('Failed to load employee records');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setEditFormData({
      employee_number: employee.employee_number || '',
      payroll_name: employee.payroll_name || '',
      frappe_name: employee.frappe_name || '',
      short_name: employee.short_name || '',
      phone: employee.phone || '',
      email: employee.email || '',
      department: employee.department || '',
      job_role: employee.job_role || '',
      job_level: employee.job_level || '',
      employment_type: employee.employment_type || '',
      primary_store: employee.primary_store || '',
      primary_store_name: employee.primary_store_name || '',
      status: employee.status || 'Active',
      employee_id_kf: employee.employee_id_kf || '',
      employee_id_koo: employee.employee_id_koo || '',
      dob: employee.dob || '',
      is_admin: employee.is_admin || false,
      last_rate_adjustment_date: employee.last_rate_adjustment_date || null,
    });
  };

  const handleCancelEdit = () => {
    setEditingEmployee(null);
    setEditFormData({});
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // If job_role changes, reset job_level and update available levels
    if (name === 'job_role') {
      setEditFormData({
        ...editFormData,
        job_role: value,
        job_level: '', // Reset level when role changes
      });
    } else {
      setEditFormData({
        ...editFormData,
        [name]: type === 'checkbox' ? checked : value,
      });
    }
  };
  
  // Get available levels for the selected role
  const getAvailableLevels = () => {
    if (!editFormData.job_role || !JOB_ROLES[editFormData.job_role]) {
      return [];
    }
    return JOB_ROLES[editFormData.job_role];
  };

  const handleSave = async () => {
    if (!editingEmployee) return;

    try {
      setIsSaving(true);
      setError('');

      // Validate required fields
      if (!editFormData.employee_number) {
        setError('Employee number is required');
        return;
      }

      // Prepare update data (exclude employee_number as it's the key)
      const updateData = { ...editFormData };
      delete updateData.employee_number; // Don't update the primary key

      // Convert empty strings to null for optional fields
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === '') {
          updateData[key] = null;
        }
      });

      const { error: updateError } = await supabase
        .from('employees')
        .update(updateData)
        .eq('employee_number', editingEmployee.employee_number);

      if (updateError) {
        throw updateError;
      }

      // Refresh the list
      await fetchEmployees();
      setEditingEmployee(null);
      setEditFormData({});
      alert('Employee record updated successfully!');
    } catch (err) {
      console.error('Error updating employee:', err);
      setError(`Failed to update employee: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (emp.employee_number && emp.employee_number.toLowerCase().includes(searchLower)) ||
      (emp.payroll_name && emp.payroll_name.toLowerCase().includes(searchLower)) ||
      (emp.frappe_name && emp.frappe_name.toLowerCase().includes(searchLower)) ||
      (emp.phone && emp.phone.includes(searchTerm)) ||
      (emp.email && emp.email.toLowerCase().includes(searchLower)) ||
      (emp.department && emp.department.toLowerCase().includes(searchLower)) ||
      (emp.job_role && emp.job_role.toLowerCase().includes(searchLower)) ||
      (emp.job_level && emp.job_level.toLowerCase().includes(searchLower))
    );
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-gray-500">Loading employee records...</div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Employee Management</h2>
            <p className="mt-1 text-sm text-gray-500">
              View and update staff records ({employees.length} total employees)
            </p>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="px-6 py-4 border-b border-gray-200">
        <input
          type="text"
          placeholder="Search by name, employee number, phone, email, department, job role, or job level..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      {/* Error Display */}
      {error && (
        <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Employee List */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Employee #
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Department
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Job Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Job Level
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredEmployees.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                  {searchTerm ? 'No employees found matching your search.' : 'No employees found.'}
                </td>
              </tr>
            ) : (
              filteredEmployees.map((employee) => (
                <tr key={employee.id || employee.employee_number} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {employee.employee_number || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {employee.payroll_name || employee.frappe_name || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>{employee.phone || 'N/A'}</div>
                    <div className="text-xs text-gray-400">{employee.email || ''}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {employee.department || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {employee.job_role || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {employee.job_level || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        employee.status === 'Active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {employee.status || 'Active'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(employee)}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {editingEmployee && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center px-5 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-t-lg">
              <h3 className="text-lg font-semibold">
                Edit Employee: {editingEmployee.payroll_name || editingEmployee.employee_number}
              </h3>
              <button
                onClick={handleCancelEdit}
                className="text-white hover:text-gray-200 transition-colors p-1 rounded hover:bg-indigo-800"
                title="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Form Content */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="space-y-4">
                {/* Basic Information Section */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 pb-1 border-b">Basic Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Employee Number (read-only) */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Employee Number *
                      </label>
                      <input
                        type="text"
                        value={editFormData.employee_number || ''}
                        disabled
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                      />
                    </div>

                    {/* Payroll Name */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Payroll Name
                      </label>
                      <input
                        type="text"
                        name="payroll_name"
                        value={editFormData.payroll_name || ''}
                        onChange={handleInputChange}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    {/* Frappe Name */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Frappe Name
                      </label>
                      <input
                        type="text"
                        name="frappe_name"
                        value={editFormData.frappe_name || ''}
                        onChange={handleInputChange}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    {/* Short Name */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Short Name
                      </label>
                      <input
                        type="text"
                        name="short_name"
                        value={editFormData.short_name || ''}
                        onChange={handleInputChange}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Phone
                      </label>
                      <input
                        type="text"
                        name="phone"
                        value={editFormData.phone || ''}
                        onChange={handleInputChange}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={editFormData.email || ''}
                        onChange={handleInputChange}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    {/* Department */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Department
                      </label>
                      <input
                        type="text"
                        name="department"
                        value={editFormData.department || ''}
                        onChange={handleInputChange}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Job Information Section */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 pb-1 border-b">Job Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

                    {/* Job Role */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Job Role
                      </label>
                      <select
                        name="job_role"
                        value={editFormData.job_role || ''}
                        onChange={handleInputChange}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select Role...</option>
                        {Object.keys(JOB_ROLES).map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </div>

                    {/* Job Level */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Job Level
                      </label>
                      <select
                        name="job_level"
                        value={editFormData.job_level || ''}
                        onChange={handleInputChange}
                        disabled={!editFormData.job_role}
                        className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                          !editFormData.job_role ? 'bg-gray-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <option value="">Select Level...</option>
                        {getAvailableLevels().map(level => (
                          <option key={level} value={level}>{level}</option>
                        ))}
                      </select>
                    </div>

                    {/* Employment Type */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Employment Type
                      </label>
                      <select
                        name="employment_type"
                        value={editFormData.employment_type || ''}
                        onChange={handleInputChange}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select...</option>
                        <option value="Full-time">Full-time</option>
                        <option value="Part-time">Part-time</option>
                        <option value="Contract">Contract</option>
                        <option value="Temporary">Temporary</option>
                      </select>
                    </div>

                    {/* Status */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Status
                      </label>
                      <select
                        name="status"
                        value={editFormData.status || 'Active'}
                        onChange={handleInputChange}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Terminated">Terminated</option>
                      </select>
                    </div>

                    {/* Last Rate Adjustment Date */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Last Rate Adjustment
                      </label>
                      <input
                        type="date"
                        name="last_rate_adjustment_date"
                        value={editFormData.last_rate_adjustment_date ? editFormData.last_rate_adjustment_date.split('T')[0] : ''}
                        onChange={handleInputChange}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Employee IDs & Store Information Section */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 pb-1 border-b">Employee IDs & Store</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

                    {/* Employee ID KF */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Employee ID (KF)
                      </label>
                      <input
                        type="text"
                        name="employee_id_kf"
                        value={editFormData.employee_id_kf || ''}
                        onChange={handleInputChange}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    {/* Employee ID KOO */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Employee ID (KOO)
                      </label>
                      <input
                        type="text"
                        name="employee_id_koo"
                        value={editFormData.employee_id_koo || ''}
                        onChange={handleInputChange}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    {/* Primary Store */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Primary Store
                      </label>
                      <input
                        type="text"
                        name="primary_store"
                        value={editFormData.primary_store || ''}
                        onChange={handleInputChange}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    {/* Primary Store Name */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Primary Store Name
                      </label>
                      <input
                        type="text"
                        name="primary_store_name"
                        value={editFormData.primary_store_name || ''}
                        onChange={handleInputChange}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    {/* Date of Birth */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Date of Birth
                      </label>
                      <input
                        type="date"
                        name="dob"
                        value={editFormData.dob || ''}
                        onChange={handleInputChange}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    {/* Is Admin */}
                    <div className="flex items-center pt-6">
                      <input
                        type="checkbox"
                        name="is_admin"
                        checked={editFormData.is_admin || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-xs font-medium text-gray-700">
                        Admin Access
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end space-x-3">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors font-medium"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagement;

